import { useMemo, useState, type JSX } from "react";

import * as Cmp from "@impero-com/common/utils/comparison";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { RateGauge, TSTPagination } from "@impero-com/ui";
import {
    createColumnHelper,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortDirection,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";
import { Select } from "antd";
import moment, { Moment } from "moment-timezone";

import {
    ExistingControlResultPaginatedListDto as ControlResultPaginatedListDto,
    ExistingControlResult,
    FutureControlResult,
    FutureControlResultPaginatedListDto,
    DashboardFilters,
    ControlProgramOverviewResourceListDto as ControlProgramOverviewResources,
    ControlProgramId,
    ControlProgramTagId,
    ControlProgramTagCategoryView,
    ControlProgramTagView,
    EntityView,
} from "api/generated/model";
import { ActivityNumbers } from "components/activityPieChart";
import { ActivityStackedBarChart } from "components/activityStackedBarChart";
import { EntitiesCell } from "components/entitiesCell/EntitiesCell";
import { DateRange } from "components/filters/dateRange";
import { Tag, TagCategory, TagsData } from "components/tag/types";
import { TagsCell } from "components/tagsCell/TagsCell";
import IconFolder from "img/icon-folder-dotted-line.svg";
import { buildControlProgramViewUrl } from "org/control/controlProgram/urlUtils";
import { useControlProgramsSuspenseQuery } from "org/control/queries/useControlProgramsQuery";
import {
    computeActivitiesCountedByStatus,
    computeCompletionRatePercentage,
    computeOnTimeRatePercentage,
} from "utils/controlResults/activityStatusUtils";
import { IdMap } from "utils/idMap";

import { useBuildActivitiesUrl } from "../hooks/useBuildActivitiesUrl";
import { useDashboardFilteredData } from "../hooks/useDashboardFilteredData";

import * as theme from "./DashboardControlProgramOverview.css";
import { SortableHeader } from "./shared/sortableHeader";
import { Table } from "./shared/table";
import { TableWidgetLayout } from "./shared/tableWidgetLayout";

interface DashboardControlProgramOverviewProps {
    filters: DashboardFilters;
    controlEntitiesEnabled: boolean;
}

export function DashboardControlProgramOverview({
    filters,
    controlEntitiesEnabled,
}: DashboardControlProgramOverviewProps) {
    const { t } = useTranslation();
    const { data: controlProgramsDto } = useControlProgramsSuspenseQuery();

    const {
        filteredControlResultsDto: controlResultPaginatedListDto,
        filteredFutureControlResultsDto: futureControlResultsPaginatedListDto,
        dateRange,
    } = useDashboardFilteredData(filters);

    const controlProgramTableItems = useMemo(() => {
        const now = moment();
        const items = computeControlProgramTableItems(
            controlResultPaginatedListDto,
            controlProgramsDto,
            futureControlResultsPaginatedListDto,
            now
        );
        return items;
    }, [
        controlResultPaginatedListDto,
        controlProgramsDto,
        futureControlResultsPaginatedListDto,
    ]);

    return (
        <TableWidgetLayout
            headerTitle={t("DashboardControlProgramOverview_Header")}
            headerSubtitle={filters.timePeriod}
        >
            <ControlProgramTable
                controlProgramTableItems={controlProgramTableItems}
                dateRange={dateRange}
                dashboardFilters={filters}
                controlEntitiesEnabled={controlEntitiesEnabled}
            />
        </TableWidgetLayout>
    );
}

/**
 * Every control program item represents a row in the table
 */
interface ControlProgramTableItem {
    /*
     * Control program ID
     */
    id: number;

    /**
     * Control program name
     */
    name: string;

    /**
     * Entities linked to the program
     */
    entities: Array<{ id: number; name: string }>;

    /**
     * Control program tags and tag categories.
     */
    tagsData: TagsData;

    /**
     * Value between 0 and 100 or null if there are no activities
     * (e.g. control program has no activities or all activities are pending).
     */
    completionRatePercentage: number | null;

    /**
     * Value between 0 and 100 or null if there are no activities
     * (e.g. control program has no activities or all activities are pending).
     */
    onTimeRatePercentage: number | null;

    /**
     * Number of activities in the control program counted by status.
     */
    activitiesCountedByStatus: ActivityNumbers;

    /**
     * Total number of control activities for the control program
     * (with respected to the filters applied in the dashboard).
     */
    activitiesCount: number;
}

interface ControlProgramTableProps {
    controlProgramTableItems: Array<ControlProgramTableItem>;
    dateRange: DateRange | null;
    dashboardFilters: DashboardFilters;
    controlEntitiesEnabled: boolean;
}

function ControlProgramTable({
    controlProgramTableItems,
    dateRange,
    dashboardFilters,
    controlEntitiesEnabled,
}: ControlProgramTableProps) {
    const { t } = useTranslation();
    const columnHelper = createColumnHelper<ControlProgramTableItem>();
    const [selectedSortingStatus, setSelectedSortingStatus] =
        useState<StatusKey>("all");
    const [sorting, setSorting] = useState<SortingState>([
        { id: "activitiesCountedByStatus", desc: true }, // Sort by the descending order by the number of activities
    ]);

    const columnName = columnHelper.accessor("name", {
        header: () => t("DashboardControlProgramOverview_Name"),
        cell: (cellContext) => {
            const cp = cellContext.row.original;
            return <CellName controlProgram={cp} />;
        },
        enableSorting: true,
    });
    const columnEntities = columnHelper.accessor("entities", {
        header: () => t("DashboardControlProgramOverview_Entities"),
        cell: (cellContext) => {
            const { entities } = cellContext.row.original;
            return <EntitiesCell entities={entities} />;
        },
        enableColumnFilter: true,
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
            const a = rowA.original.entities.length;
            const b = rowB.original.entities.length;
            return Cmp.primitive(a, b);
        },
    });
    const columnTags = columnHelper.accessor("tagsData", {
        header: () => t("DashboardControlProgramOverview_Tags"),
        cell: (cellContext) => {
            const cp = cellContext.row.original;
            return (
                <TagsCell
                    tags={cp.tagsData.tags.map((tag) => ({
                        name: tag.name,
                        category:
                            cp.tagsData.tagCategories.find(
                                (category) => category.id === tag.tagCategoryId
                            )?.name ?? "",
                    }))}
                />
            );
        },
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
            const a = rowA.original.tagsData.tags.length;
            const b = rowB.original.tagsData.tags.length;
            return Cmp.primitive(a, b);
        },
        filterFn: (row, _columnId, filterValue: string[]) => {
            if (!filterValue?.length) {
                return true;
            }
            const rowTagIds = row.original.tagsData.tags.map((tag) => tag.id);
            return filterValue.some((tagId) => rowTagIds.includes(tagId));
        },
    });

    const columnCompletionRate = columnHelper.accessor(
        "completionRatePercentage",
        {
            header: () => t("DashboardControlProgramOverview_CompletionRate"),
            cell: (cellContext) => {
                const cp = cellContext.row.original;
                const percentage = cp.completionRatePercentage;
                return <RateGauge percentage={percentage} size="xs" />;
            },
            enableSorting: true,
        }
    );

    const columnOnTimeRate = columnHelper.accessor("onTimeRatePercentage", {
        header: () => t("DashboardControlProgramOverview_OnTimeRate"),
        cell: (cellContext) => {
            const cp = cellContext.row.original;
            const percentage = cp.onTimeRatePercentage;
            return <RateGauge percentage={percentage} size="xs" />;
        },
        enableSorting: true,
    });

    const columnActivities = columnHelper.accessor(
        "activitiesCountedByStatus",
        {
            header: ({ header }) => {
                const isSorted = header.column.getIsSorted();
                const canSort = header.column.getCanSort();
                return (
                    <HeaderActivities canSort={canSort} isSorted={isSorted} />
                );
            },
            cell: (cellContext) => {
                const controlProgram = cellContext.row.original;
                return (
                    <CellActivities
                        controlProgram={controlProgram}
                        dateRange={dateRange}
                        dashboardFilters={dashboardFilters}
                    />
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB) => {
                const a = getSortValue(rowA.original, selectedSortingStatus);
                const b = getSortValue(rowB.original, selectedSortingStatus);
                return Cmp.primitive(a, b);
            },
            meta: { hasCustomSortingLogic: true },
        }
    );

    const HeaderActivities = ({
        canSort,
        isSorted,
    }: {
        canSort: boolean;
        isSorted: SortDirection | false;
    }) => {
        const canSelectToBeSent = controlProgramTableItems.some(
            (item) =>
                item.activitiesCountedByStatus.pending > 0 ||
                item.activitiesCountedByStatus.toBeSent > 0
        );
        return (
            <SortableHeader
                title={t("DashboardControlProgramOverview_Activities")}
                canSort={canSort}
                isSorted={isSorted}
                className={theme.flexContainer}
            >
                <div className={theme.borderNoneContainer}>
                    <span className={theme.sortLabel}>
                        {t("DashboardControlProgramOverview_SortBy")}
                    </span>
                    <Select
                        value={selectedSortingStatus}
                        defaultValue={selectedSortingStatus}
                        options={sortingOptions.map((value) => ({
                            value,
                            label: localizeOptions(value, t),
                            disabled:
                                value === "toBeSent" && !canSelectToBeSent,
                        }))}
                        onChange={(value: StatusKey) => {
                            setSelectedSortingStatus(value);
                            setSorting([
                                {
                                    id: "activitiesCountedByStatus",
                                    desc: true,
                                },
                            ]);
                        }}
                        className={theme.sortSelect}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            </SortableHeader>
        );
    };

    const table = useReactTable({
        data: controlProgramTableItems,
        getCoreRowModel: getCoreRowModel<ControlProgramTableItem>(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        columns: [
            columnName,
            ...(controlEntitiesEnabled ? [columnEntities] : []),
            columnTags,
            columnCompletionRate,
            columnOnTimeRate,
            columnActivities,
        ],
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        meta: {
            id: "controlProgramOverview",
        },
        initialState: {
            pagination: {
                pageSize: PAGE_SIZE,
            },
        },
    });

    return (
        <>
            <Table
                table={table}
                noDataFallback={{
                    text: t("DashboardControlProgramOverview_NoData"),
                    svgIcon: IconFolder,
                }}
            />
            <TSTPagination
                table={table}
                pageLabel={t("Pagination_Page")}
                getRowCountLabel={(shown, count) =>
                    t("Pagination_RowCount", { shown, count })
                }
                getSinglePageRowCountLabel={(count) =>
                    t("Pagination_RowCountSinglePage", { count })
                }
                showPageSizeSelect={false}
                variant="inline"
                pageSizeOptions={[PAGE_SIZE]}
            />
        </>
    );
}

const PAGE_SIZE = 8;

const compareEntityNames = Cmp.string();

function computeControlProgramTableItems(
    controlResultsDto: ControlResultPaginatedListDto,
    controlProgramsDto: ControlProgramOverviewResources,
    futureControlResultsDto: FutureControlResultPaginatedListDto,
    now: Moment
): Array<ControlProgramTableItem> {
    const { controlPrograms } = controlResultsDto.linkedResources;

    const {
        tags: controlProgramTagDtos,
        tagCategories: controlProgramTagCategories,
        entities,
    } = controlProgramsDto.linkedResources;

    const tagIdsByControlProgramId: IdMap<
        ControlProgramId,
        Array<ControlProgramTagId>
    > = IdMap.fromArray({
        name: "tagIdsByControlProgramId",
        items: controlProgramsDto.content,
        getId: (cpDto) => cpDto.resource.controlProgram.id,
        getValue: (cpDto) => cpDto.resource.tagIds,
    });
    const tagsById: IdMap<ControlProgramTagId, ControlProgramTagView> =
        IdMap.fromArray({
            name: "tagsById",
            items: controlProgramTagDtos.map((tagDto) => tagDto.resource),
            getId: (tag) => tag.id,
            getValue: (tag) => tag,
        });
    const categoriesById: IdMap<string, ControlProgramTagCategoryView> =
        IdMap.fromArray({
            name: "categoriesById",
            items: controlProgramTagCategories,
            getId: (cat) => cat.id,
            getValue: (cat) => cat,
        });

    const entitiesById: IdMap<number, EntityView> = IdMap.fromArray({
        name: "entitiesById",
        items: entities,
        getId: (entity) => entity.id,
        getValue: (entity) => entity,
    });

    const entityIdsByControlProgramId: IdMap<
        ControlProgramId,
        Array<number>
    > = IdMap.fromArray({
        name: "entityIdsByControlProgramId",
        items: controlProgramsDto.content,
        getId: (cpDto) => cpDto.resource.controlProgram.id,
        getValue: (cpDto) => cpDto.resource.controlEntityIds,
    });

    const controlResultsByControlProgramId =
        buildControlResulstsByControlProgramId(controlResultsDto);

    return controlPrograms.map((controlProgramDtoContent) => {
        const cp = controlProgramDtoContent.resource;

        const tagIds = tagIdsByControlProgramId.getOrThrow(cp.id);
        const tags: Array<Tag> = tagIds.map((tagId) => {
            const tag = tagsById.getOrThrow(tagId);
            return {
                id: tag.id,
                name: tag.name,
                tagCategoryId: tag.controlProgramTagCategoryId,
            };
        });
        const controlProgramTagCategoryIds = [
            ...new Set(tags.map((tag) => tag.tagCategoryId)),
        ];
        const tagCategories: Array<TagCategory> =
            controlProgramTagCategoryIds.map((tagCategoryId) => {
                const category = categoriesById.getOrThrow(tagCategoryId);
                return {
                    id: category.id,
                    name: category.name,
                };
            });

        const tagsData: TagsData = {
            tags,
            tagCategories,
        };

        const entityIds = entityIdsByControlProgramId.getOrThrow(cp.id);
        const entities = entityIds
            .map((entityId) => {
                const { id, name } = entitiesById.getOrThrow(entityId);
                return { id, name };
            })
            .sort((a, b) => compareEntityNames(a.name, b.name));

        const cpControlResults = controlResultsByControlProgramId.getOrThrow(
            cp.id
        );
        const futureControlResults: Array<FutureControlResult> =
            futureControlResultsDto.content
                .map((crDto) => crDto.resource)
                .filter((cr) => cr.controlProgramId === cp.id);

        const activitiesCountedByStatus = computeActivitiesCountedByStatus(
            cpControlResults,
            futureControlResults,
            now
        );

        const activitiesCount = cpControlResults.length;

        const completionRatePercentage = computeCompletionRatePercentage(
            activitiesCountedByStatus
        );
        const onTimeRatePercentage = computeOnTimeRatePercentage(
            activitiesCountedByStatus
        );

        return {
            id: cp.id,
            name: cp.name,
            entities,
            tagsData,
            completionRatePercentage,
            onTimeRatePercentage,
            activitiesCountedByStatus,
            activitiesCount,
        };
    });
}

function buildControlResulstsByControlProgramId(
    controlResultsDto: ControlResultPaginatedListDto
): IdMap<ControlProgramId, Array<ExistingControlResult>> {
    const { controlPrograms } = controlResultsDto.linkedResources;
    const allControlResults: Array<ExistingControlResult> =
        controlResultsDto.content.map((crDto) => crDto.resource);

    const controlResultsByControlProgramId: IdMap<
        ControlProgramId,
        Array<ExistingControlResult>
    > = new IdMap("controlResultsByControlProgramId");
    for (const cpDto of controlPrograms) {
        const cpId: ControlProgramId = cpDto.resource.id;
        const cpControlResults = allControlResults.filter(
            (cr) => cr.controlProgramId === cpId
        );
        controlResultsByControlProgramId.set(cpId, cpControlResults);
    }
    return controlResultsByControlProgramId;
}

interface CellNameProps {
    controlProgram: ControlProgramTableItem;
}

function CellName({ controlProgram }: CellNameProps): JSX.Element {
    const { id, name } = controlProgram;
    const href = buildControlProgramViewUrl(id);

    return (
        <a href={href} className={theme.breakWord}>
            {name}
        </a>
    );
}

interface CellActivitiesProps {
    controlProgram: ControlProgramTableItem;
    dateRange: DateRange | null;
    dashboardFilters: DashboardFilters;
}

function CellActivities({
    controlProgram,
    dateRange,
    dashboardFilters,
}: CellActivitiesProps) {
    const buildActivitiesUrl = useBuildActivitiesUrl({
        dateRange,
        filteredControlProgramIds: [controlProgram.id],
        filteredTags: dashboardFilters.tags,
        filteredEntityIds: dashboardFilters.entities,
        showArchivedActivities: dashboardFilters.showArchivedActivities,
        showInactivePrograms: dashboardFilters.showInactivePrograms,
    });

    return (
        <ActivityStackedBarChart
            numbers={controlProgram.activitiesCountedByStatus}
            getActivitiesByStatusUrl={buildActivitiesUrl}
            activitiesUrl={buildActivitiesUrl(null)}
        />
    );
}

// Define sorting options for activities
const sortingOptions = [
    "all",
    "overdue",
    "pending",
    "completed",
    "completedAfterDue",
    "toBeSent",
] as const;

type SortingOptions = (typeof sortingOptions)[number];
export type StatusKey = SortingOptions;

function localizeOptions(sortingStatus: StatusKey, t: TFunction): string {
    switch (sortingStatus) {
        case "completed":
            return t("DashboardControlProgramOverview_Activities_Completed");
        case "completedAfterDue":
            return t(
                "DashboardControlProgramOverview_Activities_CompletedAfterDue"
            );
        case "overdue":
            return t("DashboardControlProgramOverview_Activities_Overdue");
        case "pending":
            return t("DashboardControlProgramOverview_Activities_Pending");
        case "all":
            return t("DashboardControlProgramOverview_Activities_Total");
        case "toBeSent":
            return t("DashboardControlProgramOverview_Activities_ToBeSent");
        default:
            return assertNever(sortingStatus);
    }
}

function getSortValue(
    item: ControlProgramTableItem,
    selectedSortingStatus: StatusKey
): number {
    switch (selectedSortingStatus) {
        case "all":
            return Object.values(item.activitiesCountedByStatus).reduce(
                (acc, val) => acc + (typeof val === "number" ? val : 0),
                0
            );
        case "overdue":
        case "pending":
        case "completed":
        case "completedAfterDue":
        case "toBeSent":
            return item.activitiesCountedByStatus[selectedSortingStatus] ?? 0;
        default:
            assertNever(selectedSortingStatus);
    }
}
